import { useCallback, useEffect, useRef, useState } from "react";
import { requestAdminConsent, ConsentError } from "./consentAuth";
import type { AppKey } from "./consentAuth";
import "./EnterpriseAppsPage.scss";

type CardState = "idle" | "loading" | "success" | "error";

interface AppCardStatus {
  state: CardState;
  detail: string;
}

interface ToastState {
  visible: boolean;
  message: string;
  tone: "info" | "success" | "error";
}

const DEFAULT_STATUS_TEXT: Record<CardState, string> = {
  idle: "",
  loading: "Awaiting consent in popup...",
  success: "Authorized",
  error: "Consent failed"
};

const INITIAL_STATUS: AppCardStatus = { state: "idle", detail: "" };

function labelFor(app: AppKey): string {
  return app === "aidenvision" ? "AidenVision" : "AidenBot";
}

const EnterpriseAppsPage = () => {
  const [botStatus, setBotStatus] = useState<AppCardStatus>(INITIAL_STATUS);
  const [visionStatus, setVisionStatus] = useState<AppCardStatus>(INITIAL_STATUS);
  const [toast, setToast] = useState<ToastState>({ visible: false, message: "", tone: "info" });
  const toastTimer = useRef<number | null>(null);

  const currentYear = new Date().getFullYear();

  const setStatus = useCallback(
    (app: AppKey, next: AppCardStatus) => {
      if (app === "aidenbot") setBotStatus(next);
      else setVisionStatus(next);
    },
    []
  );

  const showToast = useCallback((message: string, tone: ToastState["tone"] = "info") => {
    if (toastTimer.current) {
      window.clearTimeout(toastTimer.current);
    }
    setToast({ visible: true, message, tone });
    toastTimer.current = window.setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 4200);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, []);

  const handleConsent = useCallback(
    async (app: AppKey) => {
      setStatus(app, { state: "loading", detail: DEFAULT_STATUS_TEXT.loading });
      try {
        const result = await requestAdminConsent(app);
        setStatus(app, {
          state: "success",
          detail: `Authorized - ${result.username}`
        });
        showToast(`${labelFor(app)} authorized for your tenant.`, "success");
      } catch (err) {
        if (err instanceof ConsentError && err.code === "cancelled") {
          showToast("Consent cancelled.", "info");
          setStatus(app, INITIAL_STATUS);
          return;
        }
        const message =
          err instanceof ConsentError ? err.message : "Unexpected error.";
        setStatus(app, { state: "error", detail: message });
        showToast(message, "error");
      }
    },
    [setStatus, showToast]
  );

  const renderAppCard = (app: AppKey, status: AppCardStatus, children: React.ReactNode) => {
    const isLoading = status.state === "loading";
    const isSuccess = status.state === "success";
    const statusText = status.detail || DEFAULT_STATUS_TEXT[status.state];

    return (
      <section className="eap-app" data-app={app} data-state={status.state}>
        <header className="eap-app__head">
          <h2 className="eap-app__title">{labelFor(app)}</h2>
          <span className="eap-app__check" aria-hidden="true">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8.5l3.5 3.5L13 5" />
            </svg>
          </span>
        </header>

        {children}

        <button
          type="button"
          className="eap-btn"
          data-loading={isLoading ? "true" : "false"}
          data-state={isSuccess ? "success" : undefined}
          disabled={isLoading || isSuccess}
          onClick={() => handleConsent(app)}
        >
          <span className="eap-btn__label">
            {isSuccess ? "Authorized" : "Grant admin consent"}
          </span>
          <span className="eap-btn__spinner" aria-hidden="true" />
        </button>

        <p className="eap-app__status" data-state={status.state}>
          <span className="eap-status__text">{statusText}</span>
        </p>
      </section>
    );
  };

  return (
    <>
      <div
        className="enterprise-apps-page"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/b2c-templates/bg.png)` }}
      >
        <main className="eap-card" role="main">
          <div className="eap-card__logo">
            <img src="/b2c-templates/aiden-logo.svg" alt="Aiden" />
          </div>

          <h1 className="eap-card__title">Authorize Aiden enterprise apps</h1>
          <p className="eap-card__intro">
            Grant your organization access to the Aiden applications below.
          </p>
          <p className="eap-card__intro eap-card__intro--steps">
            Sign in with an account with at least the{" "}
            <a
              href="https://learn.microsoft.com/en-us/entra/identity/role-based-access-control/permissions-reference#cloud-application-administrator"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cloud Application Administrator
            </a>{" "}
            role. Review and accept the permissions requested.
          </p>

          <div className="eap-hint" role="note">
            <svg
              className="eap-hint__icon"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="8" cy="8" r="6.5" />
              <path d="M8 7v4" />
              <circle cx="8" cy="5" r="0.5" fill="currentColor" />
            </svg>
            <p>
              On the Microsoft sign-in screen, select{" "}
              <strong>Consent on behalf of your organization</strong> to approve for all users.
              Or, accept usage for yourself, and then grant access to specific{" "}
              <strong>Users and groups</strong> in the Enterprise applications section of your
              Azure Portal.
            </p>
          </div>

          <div className="eap-apps">
            {renderAppCard("aidenbot", botStatus,
              <>
                <p className="eap-app__desc">
                  AidenBot is a utility for administrators which performs multiple roles on
                  Windows endpoints, including desired state enforcement, vulnerability scanning
                  and remediation, OS deployments, and a software installation portal. It includes
                  a command-line interface for integration with Intune, SCCM, and similar endpoint
                  management systems.
                </p>

                <details className="eap-app__more">
                  <summary>Authentication details</summary>
                  <p>
                    The Microsoft Azure Enterprise App <strong>AidenBot</strong> enables
                    cloud-based authentication and must be approved for appropriate users.
                  </p>
                  <p>
                    Microsoft Entra authentication during a bare-metal OS deployment uses
                    Entra's Device Login workflow. If Device Login is disabled in your
                    organization, it should be enabled for the same group of people who are
                    allowed to use the AidenBot Enterprise App.
                  </p>
                  <p>
                    Aiden's boot environment does not include a web browser, so Device Login
                    requires a separate device, such as a phone, tablet, Mac, or PC. All
                    Entra-integrated MFA providers are supported.
                  </p>
                </details>

                <details className="eap-app__more">
                  <summary>Data collection and storage</summary>
                  <p>
                    Aiden does not save the information collected during the authentication
                    process. Aiden only uses the tenant ID value within the authentication token
                    to retrieve OS deployment configuration settings from Aiden's cloud service.
                  </p>
                </details>

                <details className="eap-app__more">
                  <summary>Permissions</summary>
                  <ul className="eap-app__perms">
                    <li>
                      <strong>View your basic profile</strong>{" "}
                      (<code>User.ReadBasic.All</code>) - Only the minimal set of user profile
                      data; does not include full profile details.
                    </li>
                    <li>
                      <strong>Maintain access to data you have given it access to</strong>{" "}
                      (<code>offline_access</code>) - Lets the app refresh its access token in
                      the background without requiring the user to sign in again.
                    </li>
                  </ul>
                </details>
              </>
            )}

            {renderAppCard("aidenvision", visionStatus,
              <>
                <p className="eap-app__desc">
                  AidenVision is Aiden's web-based reporting system which collects compliance and
                  vulnerability data from AidenBot. Enabling Single Sign-On (SSO) allows your
                  existing Microsoft Entra accounts to sign in to AidenVision.
                </p>

                <details className="eap-app__more">
                  <summary>Authentication details</summary>
                  <p>
                    The Microsoft Azure Enterprise App <strong>AidenVision</strong> enables SSO
                    for AidenVision. After enabling SSO, you do not need to create or manage
                    separate user accounts to sign in to AidenVision.
                  </p>
                </details>

                <details className="eap-app__more">
                  <summary>Data collection and storage</summary>
                  <p>
                    Instead of directly handling user authentication and managing user identities,
                    Aiden uses Microsoft's Azure AD B2C, a popular customer identity access
                    management (CIAM) solution. Only basic user profile data is collected within,
                    and secured by, Microsoft's Azure AD B2C directory for AidenVision. Aiden does
                    not save any data collected during the authentication process outside of Azure
                    AD B2C.
                  </p>
                </details>

                <details className="eap-app__more">
                  <summary>Permissions</summary>
                  <ul className="eap-app__perms">
                    <li>
                      <strong>View your basic profile</strong>{" "}
                      (<code>User.ReadBasic.All</code>) - Only the minimal set of user profile
                      data; does not include full profile details.
                    </li>
                    <li>
                      <strong>Maintain access to data you have given it access to</strong>{" "}
                      (<code>offline_access</code>) - Lets the app refresh its access token in
                      the background without requiring the user to sign in again.
                    </li>
                  </ul>
                </details>
              </>
            )}
          </div>

          <footer className="eap-card__foot">
            <span>&copy; {currentYear} Aiden Technologies, Inc.</span>
          </footer>
        </main>
      </div>

      <div
        className="eap-toast"
        role="status"
        aria-live="polite"
        data-visible={toast.visible ? "true" : "false"}
        data-tone={toast.tone}
        hidden={!toast.visible && toast.message === ""}
      >
        {toast.message}
      </div>
    </>
  );
};

export default EnterpriseAppsPage;
